<template>
  <div>
    <error-messages v-if="anyWarnings" :errors="warnings" :header-message="warningHeader" />
    <sb-table
      :column-definitions="columns"
      :rows="rows"
    >
      <sb-table-thead slot="sb-table-header">
        <sb-table-head>
          <div :class="[containsDangerousItems ? 'table-header-adjustment-icon':'']">
            {{ columnName(0) }}
          </div>
        </sb-table-head>
        <sb-table-head>{{ columnName(1) }}</sb-table-head>
        <sb-table-head>{{ columnName(2) }}</sb-table-head>
        <sb-table-head v-if="showPlatformColumn">
          {{ columnName(3) }}
        </sb-table-head>
        <sb-table-head>{{ columnName(4) }}</sb-table-head>
        <sb-table-head>
          <div>
            {{ columnName(5) }}
            <span v-if="mobileDisplay" class="lot-expiration-help" @click.prevent="showLotExpirationHelpContent()">?</span>
            <span v-else class="lot-expiration-help">
              ?
              <sb-tooltip class="dangerous-goods__tooltip" orientation="bottom">
                <p class="dangerous-goods__tooltip-title">{{ lotAndExpirationTooltipMessage }}</p>
              </sb-tooltip>
            </span>
          </div>
        </sb-table-head>
        <sb-table-head>
          <div>
            {{ columnName(6) }}
            <span v-if="!isPoweredBySubAccountUser">
              <span v-if="mobileDisplay" class="lot-expiration-help" @click.prevent="showDangerousGoodsHelpContent()">?</span>
              <span v-else class="lot-expiration-help">
                ?
                <sb-tooltip class="dangerous-goods__tooltip" orientation="bottom">
                  <p class="dangerous-goods__tooltip-title">
                    {{ $t('dangerousGoodsText1') }}
                    <a target="_blank" :href="dangerHelpLink">{{ $t('dangerousGoodsText2') }}</a>
                  </p>
                </sb-tooltip>
              </span>
            </span>
          </div>
        </sb-table-head>
        <sb-table-head>{{ columnName(7) }}</sb-table-head>
      </sb-table-thead>
      <sb-table-row slot="sb-table-row" slot-scope="{row}" :class="warningStyle(row)" :data-testid="`product-row-${row.id}`">
        <sb-table-cell>
          <span v-if="row.isDangerousGoods" class="icon-alignment">
            <dangerous-icon class="dangerous_icon" />
            <sb-tooltip class="dangerous-goods__tooltip" orientation="bottom">
              <p class="dangerous-goods__tooltip-title">Dangerous goods</p>
            </sb-tooltip>
          </span>
          <div :class="[containsDangerousItems ? 'table-data-adjustment-icon':'']">
            <router-link
              :to="{ name: productsRoute, params: { productId: row.id } }"
              target="_blank"
            >
              {{ row.id }}
            </router-link>
          </div>
        </sb-table-cell>
        <sb-table-cell>{{ row.name }}</sb-table-cell>
        <sb-table-cell>{{ row.sku }}</sb-table-cell>
        <sb-table-cell v-if="showPlatformColumn">{{ $t((row.platform) ? row.platform.name : '') }}</sb-table-cell>
        <sb-table-cell>
          <div
            v-for="quantityObj in row.quantitiesToSend"
            :key="quantityObj._id"
            class="quantities-to-send__row"
          >
            <sb-field :variation="errorVariation('quantities', row.id, quantityObj._id)">
              <sb-text-input
                :value="parsedQuantityValue(quantityObj.amount)"
                @input="(newQuantity) => handleChangeQuantity(row.id, quantityObj._id, newQuantity)"
              />
            </sb-field>
          </div>
        </sb-table-cell>
        <sb-table-cell>
          <template v-if="row.isLotProduct">
            <div
              v-for="(quantityObj, index) in row.quantitiesToSend"
              :key="quantityObj._id"
              class="quantities-to-send__row"
            >
              <sb-field :variation="errorVariation('lotInformation', row.id, quantityObj._id)">
                <sb-text-input
                  class="quantities-to-send__input"
                  :maxlength="20"
                  :value="quantityObj.lotNumber"
                  @input="(newLotNumber) => handleChangeLotNumber(row.id, quantityObj._id, newLotNumber)"
                />
                <date-picker
                  class="quantities-to-send__date-picker"
                  :variation="errorVariation('lotInformation', row.id, quantityObj._id)"
                  :value="quantityObj.expirationDate"
                  @input="(newDate) => handleDateChange(row.id, quantityObj._id, newDate)"
                />
                <a
                  v-if="row.isLotProduct && index === (row.quantitiesToSend.length - 1)"
                  href="#"
                  class="quantities-to-send__action-button quantities-to-send__action-button--space-left quantities-to-send__action-button-icon icon quantities-to-send__icon-circle plus"
                  @click.prevent="handleAddLotEntry(row.id)"
                />

                <a
                  href="#"
                  class="quantities-to-send__action-button quantities-to-send__action-button--space-left quantities-to-send__action-button-icon icon quantities-to-send__icon-circle minus"
                  @click.prevent="handleRemoveLotEntry(row.id, quantityObj._id)"
                />
              </sb-field>
            </div>
          </template>
          <template v-else>
            <a
              class="quantities-to-send__action-button quantities-to-send__action-button--bold"
              href="#"
              @click.prevent="handleProductMarkAsLot(row.id, true)"
            >Mark as lot</a>
          </template>
        </sb-table-cell>
        <sb-table-cell v-if="NewHazmatWROExperience">
          <div :style="{display:'flex', alignItems:'center'}">
            <span v-if="row.sdsStatus == 1">
              <dangerous-icon class="dangerous_icon" />
              <sb-tooltip class="dangerous-goods__tooltip" orientation="bottom">
                <p class="dangerous-goods__tooltip-title">Dangerous goods</p>
              </sb-tooltip>
            </span>
            {{ getSdsStatus(row) }}
          </div>
        </sb-table-cell>
        <sb-table-cell v-else>
          <div>{{ row.isDangerousGoods ? 'YES' : 'NO' }}</div>
        </sb-table-cell>
        <sb-table-cell>
          <div class="quantities-to-send__row quantities-to-send__action">
            <a
              class="quantities-to-send__action-button quantities-to-send__action-button--bold"
              href="#"
              @click.prevent="handleRemoveProduct(row.id)"
            >
              <img class="delete-icon" src="@assets/images/delete.png" alt="Remove">
            </a>
          </div>
        </sb-table-cell>
      </sb-table-row>
      <sb-modal
        :active="showLotInfoModal"
        :escape-trigger="true"
        :click-outside-trigger="false"
        class="wro-tooltip-modal"
        @close="handleLotInfoModalClose"
      >
        <section class="modal-body">
          <p>{{ lotAndExpirationTooltipMessage }}</p>
        </section>
      </sb-modal>
      <sb-modal
        :active="showDangerousGoodsInfoModal"
        :escape-trigger="true"
        :click-outside-trigger="false"
        class="wro-tooltip-modal"
        @close="handleDangerousGoodsInfoModalClose"
      >
        <section class="modal-body">
          <p class="dangerous-goods__tooltip-title">
            {{ $t('dangerousGoodsText1') }}
            <a target="_blank" :href="dangerHelpLink">{{ $t('dangerousGoodsText2') }}</a>
          </p>
        </section>
      </sb-modal>
    </sb-table>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import DatePicker from '@/components/utils/PopoverDatePicker'
import { ProductsEnum } from '@/enums/modules/routes'
import ShipbobAppSettings from '@/appSettings/appSetting'
import ErrorMessages from '../utils/ErrorMessages.vue'

const moduleName = 'createWarehouseReceivingOrder'
const warningHeader = 'Please review the following issues before submitting this Warehouse Receiving Order.'

export default {
  inject: ['isPoweredBySubAccountUser'],
  components: {
    'date-picker': DatePicker,
    'error-messages': ErrorMessages
  },
  props: {
    columns: {
      type: Array,
      required: true
    },
    containsDangerousItems: { // TODO: should be computed from state products?
      type: Boolean,
      required: true
    },
    mobileDisplay: {
      type: Boolean,
      required: true
    },
    rows: {
      type: Array,
      required: true
    },
    stateMachine: {
      type: Object,
      required: true
    },
    warnings: {
      type: Array,
      required: false,
      default: () => []
    },
    showPlatformColumn: { // Platform info isn't coming through from the new product catalog, hiding this in the meantime
      type: Boolean,
      required: true
    }
  },
  data: () => ({
    dangerHelpLink: ShipbobAppSettings.wroDangerousGoodsListHref,
    errors: [],
    lotAndExpirationTooltipMessage: 'Identification number/batch number assigned to a particular quantity and its expiration date',
    productsRoute: ProductsEnum.ProductDetails,
    showDangerousGoodsInfoModal: false,
    showLotInfoModal: false,
    warningHeader
  }),
  i18n: {
    messages: {
      'en-US': {
        dangerousGoodsText1: 'Check if your items fall on our',
        dangerousGoodsText2: 'dangerous goods list.'
      }
    }
  },
  computed: {
    anyWarnings () {
      return this.warnings && this.warnings.length > 0
    },
    NewHazmatWROExperience () {
      return this.$shipBobStore.getters['Inbound.NewHazmatWROExperience']
    }
  },
  methods: {
    columnName (i) {
      const val = this.columns[i].name
      return val
    },
    errorVariation (type, productId, fieldId) {
      return this.errors[this.errorKey(type, productId, fieldId)]
        ? 'error'
        : 'default'
    },
    errorKey (type, productId, fieldId) {
      return `${type}_${productId}_${fieldId}`
    },
    getQuantitiesToSend (productId, fieldId) {
      return this.rows
        .find((p) => p.id === productId)
        .quantitiesToSend.find((qts) => qts._id === fieldId)
    },
    handleAddLotEntry (id) {
      const foundSelectedProduct = this.rows.find(
        (p) => p.id === id
      )
      if (
        foundSelectedProduct &&
        foundSelectedProduct.quantitiesToSend.length >= 40
      ) { return }
      this.$shipBobStore.dispatch(moduleName + '/addQuantity', id)
      this.stateMachine.transition('INVALID')
    },
    handleChangeLotNumber (productId, fieldId, lotNumber) {
      const qts = this.getQuantitiesToSend(productId, fieldId)
      const valid = this.validateLotInfo(lotNumber, qts.expirationDate)
      this.$shipBobStore.dispatch(moduleName + '/updateLotNumber', {
        productId,
        fieldId,
        lotNumber
      })
      this.setErrors('lotInformation', productId, fieldId, valid)
      this.triggerEdit()
    },
    handleChangeQuantity (productId, fieldId, quantity) {
      const parsedValue = parseInt(quantity)
      const valid = this.validateQuantity(parsedValue)
      this.$shipBobStore.dispatch(moduleName + '/updateQuantity', {
        productId,
        fieldId,
        quantity: parsedValue
      })
      this.setErrors('quantities', productId, fieldId, valid)
      this.triggerEdit()
    },
    handleDangerousGoodsInfoModalClose () {
      this.showDangerousGoodsInfoModal = false
    },
    handleDateChange (productId, fieldId, date) {
      const qts = this.getQuantitiesToSend(productId, fieldId)
      const valid = this.validateLotInfo(qts.lotNumber, date)
      const isValidDate = this.isValidDateEntered(date)
      this.$shipBobStore.dispatch(moduleName + '/updateLotDate', {
        productId,
        fieldId,
        lotDate: date
      })
      this.setErrors(
        'lotInformation',
        productId,
        fieldId,
        valid && isValidDate
      )
      this.triggerEdit()
    },
    handleLotInfoModalClose () {
      this.showLotInfoModal = false
    },
    handleRemoveLotEntry (productId, fieldId) {
      this.$shipBobStore.dispatch(moduleName + '/removeQuantity', {
        productId,
        fieldId
      })
      this.$emit('checkDangerousGoodsV1')
      this.triggerEdit()
    },
    handleProductMarkAsLot (productId, isMarkAsLot) {
      this.$shipBobStore.dispatch(moduleName + '/productMarkAsLot', {
        productId,
        isMarkAsLot
      })
      this.$emit('checkDangerousGoodsV1')
      this.triggerEdit()
    },
    handleRemoveProduct (productId) {
      this.$shipBobStore.dispatch(moduleName + '/removeProduct', {
        productId
      })
      this.triggerEdit()
    },
    isValidDateEntered (date) { // duped
      const currentDate = new Date()
      currentDate.setHours(0, 0, 0, 0)
      const isDateValid = !isNaN(Date.parse(date)) // Date.parse will return NaN for an invalid date
      const isDateInPast =
        isDateValid &&
        new Date(date).toISOString() >= currentDate.toISOString()
      return isDateInPast && isDateValid
    },
    parsedQuantityValue (quantity) {
      if (isNaN(quantity)) return ''
      return `${quantity}`
    },
    setErrors (type, productId, fieldId, valid) {
      const errs = cloneDeep(this.errors)
      const key = this.errorKey(type, productId, fieldId)
      if (valid) {
        delete errs[key]
      } else {
        errs[key] = true
      }
      this.errors = errs
    },
    showDangerousGoodsHelpContent () {
      this.showDangerousGoodsInfoModal = true
    },
    showLotExpirationHelpContent () {
      this.showLotInfoModal = true
    },
    triggerEdit () {
      this.stateMachine.transition('EDITED')
    },
    validateLotInfo (lotNumber, lotDate) { // duped
      const parsedLotDate = lotDate || ''
      const validParsedLotDate = this.isValidDateEntered(parsedLotDate)
      return (
        (lotNumber.length > 0 &&
          (!parsedLotDate.length > 0 || validParsedLotDate)) ||
        (parsedLotDate.length > 0 && validParsedLotDate)
      )
    },
    validateQuantity (quantity) { // duped
      const parsedValue =
        typeof quantity === 'string' ? parseInt(quantity) : quantity
      return !(isNaN(parsedValue) || parsedValue <= 0)
    },
    warningStyle (row) {
      const rowHasWarning = !!this.warnings.find(warning => warning.id === parseInt(row.id))
      return rowHasWarning ? 'table-row--warning' : 'table-row'
    },
    getSdsStatus (row) {
      const status = {
        0: 'No',
        1: 'Yes - No SDS Uploaded',
        2: 'Yes - SDS Uploaded'
      }

      return status[row.sdsStatus]
    }
  }
}
</script>

<style scoped lang='scss'>
  $color-primary: rgb(36, 112, 175);
  $color-error: rgb(170, 21, 21);
  $color-white: rgb(255, 255, 255);

.dangerous_icon {
  margin-right: 7px;
  width: 15px;
  height: 15px;
  float: left;
}

.dangerous-goods {
  &__tooltip {
    padding: 1em;
    max-width: 20em;
  }
  &__tooltip-title {
    font-weight: 600;
  }
  &__tooltip-message,
  &__tooltip-title {
    display: inline;
    color: black;
  }
}

.icon:before {
  content: "";
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1.2rem;
  height: 1.2rem;
  border-radius: 50%;
  background: #2470af;
  font-size: 1rem;
  color: white;
  font-weight: bold;
}

.icon-alignment {
    position: absolute;
    left: 15px;
    height: 21px;
    display: flex;
    align-items: center;
}

.lot-expiration-help {
    height: 1.3em;
    width: 1.3em;
    background-color: #3e444e;
    border-radius: 50%;
    color: white;
    text-align: center;
    display: inline-block;
    cursor: pointer;
}

.minus:before {
  content: "\FF0D";
}

.plus:before {
  content: "\FF0B";
}

.quantities-to-send {
  &__date-picker {
    color: #2470af !important;
    margin-right: 0.6em;
    max-width: 120px;
  }
  &__row {
    margin-bottom: 1em;
    &:last-of-type {
      margin-bottom: 0;
    }
    .sb-form-field {
      flex-direction: row;
      align-items: center;
    }
  }
  &__action {
    margin-bottom: 2.3em;
    display: flex;
    align-items: center;
  }
  &__action-button {
    text-decoration: none;
    color: #2671af;
    &--bold {
      font-weight: 600;
    }
    &--space-left {
      margin-left: 0.2rem;
    }
  }
  &__action-button-icon .icon-minus-circle {
    margin-bottom: 0.2em;
  }
  &__action-button-icon {
    font-size: 1.25em;
  }
  &__buttons {
    flex-direction: column;
  }
  &__input {
    margin-right: 0.75em;
    max-width: 120px;
  }
}

.table td {
  max-width: 460px;
}

.table-data-adjustment-icon {
  margin-left: 1rem;
}

.table-header-adjustment-icon {
  margin-left: 0.5rem;
}

.table-row {
  transition: background 250ms ease;

  &--selected,
  &--edited {
    background: rgba($color-primary, 0.1);
  }

  &--disabled {
    background: darken($color: $color-white, $amount: 5);
  }

  &--warning {
    background: lighten($color: $color-error, $amount: 60);
    border-color: $color-error;
    color: $color-error;
  }
}

.wro-tooltip-modal {
  z-index: 100;
}
</style>
